import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';

interface PlayedClip {
  walkthroughId: number;
  stepIndex: number;
  narrationText: string;
}

/**
 * Plays per-step narration for the walkthrough player. Fetches (or gets the cached) MP3 from the
 * Sparta walkthrough-audio endpoint and plays it when a step opens. Audio failures never break the
 * tour: a fetch/synth error is swallowed so the walkthrough still advances silently.
 *
 * The Sparta client disables the JSON casing/date interceptors so the binary blob response is not
 * touched. Mute is persisted in localStorage so a user who turns audio off stays off across tours.
 */
@Injectable({ providedIn: 'root' })
export class WalkthroughAudioService {
  private static readonly MUTE_STORAGE_KEY = 'ups.walkthrough.audio.muted';

  private readonly http: HttpClient;

  /** True while audio is turned off. Bound by the player's mute toggle. */
  readonly muted = signal<boolean>(this.readStoredMuted());

  private currentAudio: HTMLAudioElement | null = null;
  private currentObjectUrl: string | null = null;
  private lastClip: PlayedClip | null = null;

  // Guards against a stale clip arriving after the step already changed.
  private playSequence = 0;

  constructor(clientFactory: MyHttpClientFactory) {
    this.http = clientFactory.createHttpClient(
      environment.urls!.spartaAPI!,
      true,
      ResponseCasingEnum.Default,
      { reviveDates: false, localCalendarDateBody: false }
    );
  }

  /** Play the narration for a step. Stops any current clip first. No-op when muted or text is empty. */
  async playForStep(
    walkthroughId: number | null | undefined,
    stepIndex: number,
    narrationText: string
  ): Promise<void> {
    this.stop();

    const trimmed = (narrationText ?? '').trim();
    if (walkthroughId == null || !trimmed) {
      this.lastClip = null;
      return;
    }

    this.lastClip = { walkthroughId, stepIndex, narrationText: trimmed };
    if (this.muted()) {
      return;
    }

    await this.fetchAndPlay(this.lastClip);
  }

  /** Re-play the current step's narration (ignores mute for this explicit user action). */
  async replay(): Promise<void> {
    if (!this.lastClip) {
      return;
    }
    this.stop();
    await this.fetchAndPlay(this.lastClip);
  }

  /** Toggle audio on/off. Turning it off stops the current clip; turning it on replays the step. */
  toggleMute(): void {
    const next = !this.muted();
    this.muted.set(next);
    this.writeStoredMuted(next);
    if (next) {
      this.stop();
    } else {
      void this.replay();
    }
  }

  /** Stop and release any playing clip. */
  stop(): void {
    this.playSequence++;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.src = '';
      this.currentAudio = null;
    }
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }
  }

  private async fetchAndPlay(clip: PlayedClip): Promise<void> {
    const mySequence = ++this.playSequence;
    try {
      const blob = await firstValueFrom(
        this.http.post('api/walkthrough-audio', clip, { responseType: 'blob' })
      );

      // A newer step (or a stop) superseded this request while it was in flight.
      if (mySequence !== this.playSequence) {
        return;
      }

      const url = URL.createObjectURL(blob);
      this.currentObjectUrl = url;
      const audio = new Audio(url);
      this.currentAudio = audio;
      await audio.play();
    } catch {
      // Audio is an enhancement: never let a synth/fetch/autoplay failure break the tour.
      if (mySequence === this.playSequence) {
        this.stop();
      }
    }
  }

  private readStoredMuted(): boolean {
    try {
      return (
        globalThis.localStorage?.getItem(
          WalkthroughAudioService.MUTE_STORAGE_KEY
        ) === 'true'
      );
    } catch {
      return false;
    }
  }

  private writeStoredMuted(value: boolean): void {
    try {
      globalThis.localStorage?.setItem(
        WalkthroughAudioService.MUTE_STORAGE_KEY,
        String(value)
      );
    } catch {
      // localStorage unavailable (private mode / SSR): mute stays session-only.
    }
  }
}
